import { createStore } from 'vuex'
import axios from 'axios'
import { 
  saveAuthToken, 
  getAuthToken, 
  removeAuthToken, 
  saveRefreshToken, 
  getRefreshToken, 
  removeRefreshToken,
  isTokenExpired
} from '@/utils/auth'
import apiService from '@/services/apiService'
import visModule from './modules/vis'
import assessmentsModule from './modules/assessments'
import expertsModule from './modules/experts'
import { useToast } from 'vue-toastification'

// API base URL
const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:8000/api'

export default createStore({
  state: {
    user: null,
    token: getAuthToken() || null,
    refreshToken: getRefreshToken() || null,
    loading: false,
    surveys: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0
    },
    error: null
  },
  getters: {
    isAuthenticated: state => !!state.token && !!state.user,
    isTokenExpired: () => isTokenExpired(),
    authHeader: state => ({ 
      'Authorization': `Bearer ${state.token}`
    }),
    // Add explicit admin status getters that use the fields from the API
    isAdmin: state => state.user && state.user.role === 'admin' || state.user && state.user.role === 'super_admin',
    isSuperAdmin: state => state.user && state.user.role === 'super_admin',
    // Survey getters by status
    newSurveys: state => state.surveys.filter(survey => 
      !survey.started_at && !survey.completed_at && !survey.is_expired
    ),
    completedSurveys: state => state.surveys.filter(survey => 
      survey.completed_at
    ),
    pendingSurveys: state => state.surveys.filter(survey => 
      survey.started_at && !survey.completed_at && !survey.is_expired
    ),
    pendingResponses: state => state.surveys.filter(survey => 
      survey.needs_response
    )
  },
  mutations: {
    SET_LOADING(state, isLoading) {
      state.loading = isLoading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    SET_USER(state, user) {
      state.user = user
    },
    SET_TOKEN(state, token) {
      state.token = token
      if (token) {
        saveAuthToken(token)
      } else {
        removeAuthToken()
      }
    },
    SET_REFRESH_TOKEN(state, token) {
      state.refreshToken = token
      if (token) {
        saveRefreshToken(token)
      } else {
        removeRefreshToken()
      }
    },
    SET_SURVEYS(state, surveys) {
      state.surveys = surveys
    },
    SET_PAGINATION(state, pagination) {
      state.pagination = {
        ...state.pagination,
        ...pagination
      }
    },
    CLEAR_AUTH(state) {
      state.user = null
      state.token = null
      state.refreshToken = null
      removeAuthToken()
      removeRefreshToken()
    }
  },
  actions: {
    // Global notification action so components can simply dispatch('showNotification', { type, message })
    showNotification(context, { type = 'info', message = '' } = {}) {
      const toast = useToast()
      switch (type) {
        case 'success':
          toast.success(message)
          break
        case 'error':
          toast.error(message)
          break
        case 'warning':
          toast.warning ? toast.warning(message) : toast(message)
          break
        case 'info':
        default:
          toast.info ? toast.info(message) : toast(message)
      }
    },
    // Auth actions
    async login({ commit }, credentials) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await axios.post(`${API_URL}/auth/token/`, credentials)
        console.log('Login API response:', response.data)
        const { access, refresh, user } = response.data
        
        commit('SET_TOKEN', access)
        commit('SET_REFRESH_TOKEN', refresh)
        commit('SET_USER', user)
        
        return response.data
      } catch (error) {
        const errorMessage = error.response?.data?.detail || 'Login failed. Please try again.'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // Refresh the access token
    async refreshToken({ commit, state }) {
      if (!state.refreshToken) {
        return Promise.reject('No refresh token available');
      }
      
      try {
        const response = await apiService.refreshToken(state.refreshToken);
        const { access, refresh } = response.data;
        
        commit('SET_TOKEN', access);
        // If a new refresh token is provided, update it too
        if (refresh) {
          commit('SET_REFRESH_TOKEN', refresh);
        }
        
        return access;
      } catch (error) {
        commit('CLEAR_AUTH');
        throw error;
      }
    },
    
    async register({ commit }, userData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await axios.post(`${API_URL}/auth/register/`, userData)
        return response.data
      } catch (error) {
        const errorMessage = error.response?.data?.detail || 'Registration failed. Please try again.'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchUserProfile({ commit, getters }) {
      try {
        commit('SET_LOADING', true)
        
        const response = await axios.get(`${API_URL}/auth/profile/`, {
          headers: getters.authHeader
        })
        
        commit('SET_USER', response.data)
        return response.data
      } catch (error) {
        if (error.response?.status === 401) {
          commit('CLEAR_AUTH')
        }
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async logout({ commit, state }) {
      try {
        if (state.refreshToken) {
          // Call the blacklist endpoint to invalidate the refresh token
          await apiService.logout();
        }
      } catch (error) {
        console.error('Error during logout:', error);
      } finally {
        commit('CLEAR_AUTH');
      }
    },
    
    // Survey actions
    async fetchSurveys({ commit, getters }, { page = 1, filters = {} } = {}) {
      try {
        commit('SET_LOADING', true)
        
        const params = {
          page,
          ...filters
        }
        
        const response = await axios.get(`${API_URL}/projects/surveys/`, {
          headers: getters.authHeader,
          params
        })
        
        commit('SET_SURVEYS', response.data.results)
        commit('SET_PAGINATION', {
          currentPage: page,
          totalPages: Math.ceil(response.data.count / response.data.page_size),
          totalItems: response.data.count
        })
        
        return response.data
      } catch (error) {
        console.error('Error fetching surveys:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async getSurveyDetail({ commit, getters }, surveyId) {
      try {
        commit('SET_LOADING', true)
        
        const response = await axios.get(`${API_URL}/projects/surveys/${surveyId}/`, {
          headers: getters.authHeader
        })
        
        return response.data
      } catch (error) {
        console.error('Error fetching survey detail:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async startSurvey({ commit, getters }, surveyId) {
      try {
        commit('SET_LOADING', true)
        
        const response = await axios.post(`${API_URL}/projects/surveys/${surveyId}/start/`, {}, {
          headers: getters.authHeader
        })
        
        return response.data
      } catch (error) {
        console.error('Error starting survey:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    }
  },
  modules: {
    vis: visModule,
    assessments: assessmentsModule,
    experts: expertsModule
  }
}) 